import { Injectable } from '@angular/core';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpErrorResponse
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { AuthService } from './auth.service';
import { Router } from '@angular/router';
import { AUTH_CALL, SessionService } from './session.service';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  constructor(
    private authService: AuthService,
    private session: SessionService,
    private router: Router
  ) {}

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    // Skip token for login endpoint
    if (req.url.includes('/auth/login')) {
      return next.handle(req);
    }

    return next.handle(this.withAuthHeaders(req)).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status !== 401 || AUTH_CALL.test(req.url)) {
          return throwError(() => error);
        }

        // Nobody to sign back in as (signed out in another tab): the old
        // behaviour, straight to the login screen.
        if (!this.session.canRelogin()) {
          this.authService.logout();
          this.router.navigate(['']);
          return throwError(() => error);
        }

        // Session ran out mid-work. Keep the page (and the unsaved form) as
        // it is, ask for the password, then send this request again.
        const reason =
          error.error?.message === 'JWT token has expired'
            ? 'Your session has expired.'
            : 'Your session is no longer valid.';
        return this.session.requestRelogin(reason).pipe(
          switchMap((signedIn) =>
            signedIn
              ? next.handle(this.withAuthHeaders(req))
              : throwError(() => error),
          ),
        );
      })
    );
  }

  private withAuthHeaders(req: HttpRequest<any>): HttpRequest<any> {
    const token = localStorage.getItem('authToken');

    // The store/warehouse selected in the header switcher travels with every
    // request so the backend scopes numbering (PO/GRN/TO/...), lists and
    // channel behavior to the SELECTED store instead of guessing
    // warehouse-first from the user's access rows.
    const activeStoreId = localStorage.getItem('storeId');
    const activeLocationId = localStorage.getItem('activeLocationId');

    // Super admin working inside a workspace: the backend honours this header
    // for SUPER_ADMIN only and scopes every query to that tenant.
    const tenantId = this.authService.getSelectedTenantId();

    const headers: { [name: string]: string } = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    if (activeStoreId) headers['x-active-store-id'] = activeStoreId;
    if (activeLocationId) headers['x-active-location-id'] = activeLocationId;
    // A call that names its own tenant (onboarding acting on the workspace it
    // just created) keeps it.
    if (tenantId && !req.headers.has('x-tenant-id')) headers['x-tenant-id'] = tenantId;

    return Object.keys(headers).length
      ? req.clone({ setHeaders: headers })
      : req;
  }
}
